import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { SectionHeading } from '../ui/Panels';
import { chefs } from '../../data/chefs';
import { spring } from '../../utils/motion';
import { cn } from '../../utils/cn';
interface Zone {
  id: string;
  label: string;
  /** Matches the start of the chef service-area names that belong to this zone. */
  match: string[];
  x: number;
  y: number;
}
const zones: Zone[] = [{
  id: 'chatuchak',
  label: 'Chatuchak & Lat Phrao',
  match: ['Chatuchak'],
  x: 258,
  y: 58
}, {
  id: 'ari',
  label: 'Phaya Thai & Ari',
  match: ['Phaya Thai'],
  x: 206,
  y: 108
}, {
  id: 'lumphini',
  label: 'Ratchadamri & Lumphini',
  match: ['Ratchadamri'],
  x: 224,
  y: 168
}, {
  id: 'sukhumvit',
  label: 'Sukhumvit mid',
  match: ['Sukhumvit mid', 'Lower Sukhumvit'],
  x: 296,
  y: 152
}, {
  id: 'onnut',
  label: 'On Nut & Phra Khanong',
  match: ['On Nut'],
  x: 350,
  y: 198
}, {
  id: 'silom',
  label: 'Sathorn & Silom',
  match: ['Sathorn & Silom', 'Silom & Sathorn'],
  x: 186,
  y: 214
}, {
  id: 'oldtown',
  label: 'Old town & Yaowarat',
  match: ['Old town'],
  x: 124,
  y: 150
}, {
  id: 'riverside',
  label: 'Riverside & Bang Rak',
  match: ['Riverside'],
  x: 126,
  y: 238
}, {
  id: 'thonburi',
  label: 'Thonburi',
  match: ['Thonburi'],
  x: 66,
  y: 196
}];
function coverage(zone: Zone) {
  const covering = chefs.filter(chef => chef.serviceAreas.some(area => zone.match.some(m => area.name.startsWith(m))));
  const inHome = covering.filter(chef => chef.serviceAreas.some(area => zone.match.some(m => area.name.startsWith(m)) && area.fulfillment.includes('in_home'))).length;
  return {
    total: covering.length,
    inHome,
    delivery: covering.length - inHome,
    chefs: covering
  };
}
export function ServiceAreaVector() {
  const [active, setActive] = useState<string | null>(null);
  const reduce = useReducedMotion();
  const rows = zones.map(zone => ({
    zone,
    ...coverage(zone)
  })).filter(row => row.total > 0).sort((a, b) => b.total - a.total);
  return <section aria-labelledby="areas-heading" className="border-y border-hairline bg-oat" data-id="element-598" data-mp="3ckyyzre7gq53">
      <div className="mx-auto max-w-shell px-5 py-16 lg:px-8 lg:py-20" data-id="element-599" data-mp="2yttvge7pvdph">
        <SectionHeading level={2} title="Where chefs cover in Bangkok" hint="Solid zones include travel in the price. Everything outside adds a per-kilometre line to the quote." data-id="element-600" data-mp="2ytlikmblg8ea" />

        <div className="mt-9 grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:gap-10" data-id="element-601" data-mp="2oy0k2y5qhq9a">
          <figure className="rounded-2xl border border-hairline bg-surface p-4" data-id="element-602" data-mp="2tseb6odd5b85">
            <svg viewBox="0 0 420 320" className="h-auto w-full" role="img" aria-label="Simplified map of Bangkok showing the districts our chefs cover" data-id="element-603" data-mp="2fx2ewcjk7cp4">
              {/* Chao Phraya */}
              <path d="M30 -10 C70 60, 60 110, 100 150 C140 190, 90 230, 110 280 C120 305, 150 315, 170 330" fill="none" stroke="rgb(var(--c-primary) / 0.22)" strokeWidth="16" strokeLinecap="round" data-id="element-604" data-mp="i5hkw6ugjn1l" />
              {/* Rail lines */}
              <path d="M258 30 L258 120 L300 168 L360 210" fill="none" stroke="rgb(var(--c-hairline))" strokeWidth="2" strokeDasharray="6 6" data-id="element-605" data-mp="2cemrv88ukkqu" />
              <path d="M120 150 L224 168 L300 152" fill="none" stroke="rgb(var(--c-hairline))" strokeWidth="2" strokeDasharray="6 6" data-id="element-606" data-mp="2hc53j6e91e2j" />
              <path d="M186 214 L224 168 L206 108" fill="none" stroke="rgb(var(--c-hairline))" strokeWidth="2" strokeDasharray="6 6" data-id="element-607" data-mp="2vav1eo8w7hry" />

              {rows.map(({
              zone,
              total,
              inHome
            }) => {
              const isActive = active === zone.id;
              const radius = 16 + total * 4;
              return <g key={zone.id} onMouseEnter={() => setActive(zone.id)} onMouseLeave={() => setActive(null)} className="cursor-pointer" data-id="element-608" data-mp="w0d3ie4luw00">
                    <motion.circle cx={zone.x} cy={zone.y} r={radius} animate={reduce ? undefined : {
                  scale: isActive ? 1.06 : 1
                }} style={{
                  transformOrigin: `${zone.x}px ${zone.y}px`
                }} transition={spring.snappy} fill={isActive ? 'rgb(var(--c-action) / 0.16)' : 'rgb(var(--c-primary) / 0.1)'} stroke={isActive ? 'rgb(var(--c-action))' : 'rgb(var(--c-primary) / 0.5)'} strokeWidth={isActive ? 2 : 1.2} strokeDasharray={inHome === 0 ? '4 4' : undefined} data-id="element-609" data-mp="17e5djaqegzyf" />
                    <circle cx={zone.x} cy={zone.y} r="3" fill={isActive ? 'rgb(var(--c-action))' : 'rgb(var(--c-primary))'} data-id="element-610" data-mp="3s2jkpcujetuq" />
                    <text x={zone.x} y={zone.y + radius + 13} textAnchor="middle" className="font-sans" fontSize="10" fontWeight="600" fill="rgb(var(--c-ink-2))" data-id="element-611" data-mp="3pkhlfpbnc6fi">
                      {zone.label.split(' & ')[0]}
                    </text>
                  </g>;
            })}
            </svg>
            <figcaption className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 px-1 text-[12px] text-ink-3" data-id="element-612" data-mp="31un8z8v8ojvs">
              <span className="flex items-center gap-1.5" data-id="element-613" data-mp="2wuhfro9bmpbl">
                <span className="h-2.5 w-2.5 rounded-full border border-primary bg-primary/15" aria-hidden="true" data-id="element-614" data-mp="2wsxvznoghw52" />
                Chefs cook on site
              </span>
              <span className="flex items-center gap-1.5" data-id="element-615" data-mp="2mwl7r5bokahp">
                <span className="h-2.5 w-2.5 rounded-full border border-dashed border-primary" aria-hidden="true" data-id="element-616" data-mp="2iuzngywtgtxh" />
                Delivery only
              </span>
              <span data-id="element-617" data-mp="28cqqqpgvt823">Circle size = chefs covering the zone</span>
            </figcaption>
          </figure>

          <ul className="grid gap-2 self-start sm:grid-cols-2 lg:grid-cols-1" data-id="element-618" data-mp="1z9pmkubktr12">
            {rows.map(({
            zone,
            total,
            inHome,
            delivery,
            chefs: covering
          }) => <li key={zone.id} data-id="element-619" data-mp="249c9jvh7iln2">
                <Link to={`/discover?where=${encodeURIComponent(zone.label)}`} onMouseEnter={() => setActive(zone.id)} onMouseLeave={() => setActive(null)} onFocus={() => setActive(zone.id)} onBlur={() => setActive(null)} className={cn('flex items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-colors duration-150 ease-out', active === zone.id ? 'border-action bg-surface' : 'border-hairline bg-surface/70 hover:border-primary/40')} data-id="element-620" data-mp="2rydcj379t4ky">
                  <span className="min-w-0" data-id="element-621" data-mp="3p1pzgo42737j">
                    <span className="block text-sm font-medium text-ink" data-id="element-622" data-mp="3p0euwm2shrfd">{zone.label}</span>
                    <span className="block truncate text-[12px] text-ink-3" data-id="element-623" data-mp="3kz3cb0ic26go">
                      {covering.slice(0, 3).map(chef => chef.name.split(' ')[0]).join(', ')}
                      {covering.length > 3 ? ` +${covering.length - 3}` : ''}
                    </span>
                  </span>
                  <span className="shrink-0 text-right" data-id="element-624" data-mp="323whbyyiie21">
                    <span className="tnum block font-display text-lg font-semibold text-ink" data-id="element-625" data-mp="3g2me1dhf6bpv">{total}</span>
                    <span className="block text-[11px] text-ink-3" data-id="element-626" data-mp="3b2gr2am3hde4">
                      {inHome} on site · {delivery} delivery
                    </span>
                  </span>
                </Link>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}